/**
 * @documental
 * @export Field
 * @name
 * Dropdown.Field
 */
import { FC, ReactNode, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { DisabledRequiredRule, required as requiredRule } from "../validation/rules";
import { DropdownProps } from "./Dropdown";
import { Input } from "./Input";

/**
 * The `Field` component properties.
 */
export interface FieldProps
  extends FieldBaseProps<string | null>,
    Pick<DropdownProps, "onSearchChange" | "manualSearchFilter" | "onMenuScrollToBottom" | "searchInputProps"> {
  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;

  /**
   * Indicates whether the dropdown is able to be searched. If not specified defaults to `false`.
   */
  readonly searchable?: boolean;
}

/**
 * A React Hook Form compatible Dropdown component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, children, validator, required = false, defaultValue = null } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [required, validator, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input {...props} ref={ref} value={value} onChange={onChange} onFocusExit={onBlur} clearable={!required}>
          {children}
        </Input>
      </ValidityGroup>
    </SubmitGroup>
  );
};
